<script setup lang="ts">
import { ref, computed, toValue, watch, onMounted } from "vue";
import type { Router } from "vue-router";
import { useNameValidation } from "@/composables/validation/useNameValidation.js";
import { useDateOfBirthValidation } from "@/composables/validation/useDateOfBirthValidation.js";
import { getFormattedDateOfBirthValue } from "@/features/enrollment/utils.js";
import { useEmailValidation } from "@/composables/validation/useEmailValidation.js";
import { useFormattedAddresses } from "@/features/enrollment/composables.js";
import BaseInput from "@/library/BaseInput.vue";
import BaseButton from "@/library/BaseButton.vue";
import EnrollmentAgreement from "@/features/enrollment/EnrollmentAgreement.vue";
import EnrollmentAddressInput from "@/features/enrollment/EnrollmentAddressInput.vue";
import EnrollmentTag from "@/features/enrollment/EnrollmentTag.vue";
import EnrollmentTags from "@/features/enrollment/EnrollmentTags.vue";
import PageEnrollmentAddressesManual from "@/routes/enrollment/PageEnrollmentAddressesManual.vue";
import BaseText from "@/library/BaseText.vue";
import DataDeleteService from "@/api/actions/data-delete-service.js";
import { toApiPayload } from "@/features/enrollment/data-utils.js";
import { useRouter } from "vue-router";
import { posthogCapture } from "@/scripts/posthog.js";
import { PH_EVENT_USER_SUBMITTED_DD_SUBMISSION_FORM } from "@/scripts/posthogEvents.js";
import { useColorScheme } from "@/composables/useColorScheme";
import { useToast } from "@/composables/useToast.js";

export interface EnrollmentAddress {
  address1?: string;
  address2?: string | null;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

interface AutofillData {
  name?: {
    first?: string;
    last?: string;
  };
  dob?: string;
  email_addresses?: string[];
  addresses?: EnrollmentAddress[];
  wasAutofilled?: boolean;
}

interface Props {
  autofillData?: AutofillData;
}

interface EnrollmentRequest {
  name: {
    first?: string;
    last?: string;
  };
  dob?: string;
  addresses?: EnrollmentAddress[];
  email_addresses?: string[];
}

interface FormattedAddress {
  address: EnrollmentAddress;
  display: string;
}

// Composables
const { colorScheme } = useColorScheme();
const toast = useToast();
const router: Router = useRouter();

// Props
const props = withDefaults(defineProps<Props>(), {
  autofillData: () => ({}),
});

// Lifecycle
onMounted(() =>
  posthogCapture("user_viewed_enrollment_form_exposures", {
    theme: colorScheme.value,
  })
);

// Models with proper typing
const firstName = defineModel<string>("firstName", { default: "" });
const lastName = defineModel<string>("lastName", { default: "" });
const dob = defineModel<string>("dob", {
  default: "",
  get: getFormattedDateOfBirthValue,
});
const email = defineModel<string>("email", { default: "" });
const addresses = defineModel<EnrollmentAddress[]>("addresses", {
  default: () => [],
});
const agreesWithTerms = defineModel<boolean>("agreesWithTerms", {
  default: false,
});

// Set default values after props are available
const hasPrefilledData = ref(false);
watch(
  () => props.autofillData,
  (newAutofillData: AutofillData | undefined) => {
    const autofillData = toValue(newAutofillData);
    if (autofillData?.wasAutofilled && !hasPrefilledData.value) {
      firstName.value = autofillData.name?.first || "";
      lastName.value = autofillData.name?.last || "";
      dob.value = autofillData.dob || "";
      email.value = autofillData.email_addresses?.[0] || "";
      // addresses.value = autofillData.addresses || []; // TODO: uncomment this when unmasking addresseses from microservice is working
      hasPrefilledData.value = true;
    }
  },
  { immediate: true, deep: true }
);

const {
  error: firstNameError,
  validate: validateFirstName,
  validateDebounced: validateFirstNameDebounced,
} = useNameValidation(firstName);

const {
  error: lastNameError,
  validate: validateLastName,
  validateDebounced: validateLastNameDebounced,
} = useNameValidation(lastName);

const {
  error: dateOfBirthError,
  validate: validateDateOfBirth,
  validateDebounced: validateDateOfBirthDebounced,
} = useDateOfBirthValidation(dob);

const {
  error: emailError,
  validate: validateEmail,
  validateDebounced: validateEmailDebounced,
} = useEmailValidation(email);

// State refs
const isSubmitting = ref<boolean>(false);
const addressError = ref<string | null>(null);
const addressString = ref<string | undefined>(undefined);

// Computed properties
const formattedPrimaryAddress = computed((): FormattedAddress | null => {
  if (addresses.value?.length > 0) {
    const formattedAddresses = useFormattedAddresses(addresses);
    return toValue(formattedAddresses)?.[0] || null;
  }
  return null;
});

// Validation functions
function validateAddresses(): boolean {
  if (addresses.value?.length === 0) {
    addressError.value = "Please add an address";
  }

  return !addressError.value;
}

// Event handlers
function onRemoveAddress(): void {
  addresses.value = [];
}
const manualAddressValid = ref(false);
function onAddAddress(address: EnrollmentAddress): void {
  addresses.value = [address];
  if (showAddressManual.value) {
    manualAddressValid.value = true;
  }
  addressString.value = "";
}

const isFormValid = computed(() => {
  const baseFormValid =
    firstName.value?.length &&
    lastName.value?.length &&
    dob.value?.length &&
    email.value?.length &&
    addresses.value?.length > 0 &&
    !firstNameError.value &&
    !lastNameError.value &&
    !dateOfBirthError.value &&
    !emailError.value &&
    !addressError.value &&
    agreesWithTerms.value;

  // If manual address component is shown, also check its validity
  if (showAddressManual.value && manualAddressComponent.value) {
    return baseFormValid && Boolean(manualAddressComponent.value.isFormValid);
  }

  return baseFormValid;
});

// Component refs
const firstNameInput = ref<HTMLInputElement | null>(null);
const lastNameInput = ref<HTMLInputElement | null>(null);
const emailInput = ref<HTMLInputElement | null>(null);
const dobInput = ref<HTMLInputElement | null>(null);
const addressInput = ref<HTMLInputElement | null>(null);
const manualAddressComponent = ref<InstanceType<
  typeof PageEnrollmentAddressesManual
> | null>(null);

// UI state
const showAddressManual = ref<boolean>(false);

function toggleAddressManual(): void {
  showAddressManual.value = !showAddressManual.value;
  if (showAddressManual.value) {
    manualAddressValid.value = false;
  }
}

function validateForm(): boolean {
  validateFirstName();
  validateLastName();
  validateDateOfBirth();
  validateEmail();
  validateAddresses();

  const baseFormValid =
    !firstNameError.value &&
    !lastNameError.value &&
    !dateOfBirthError.value &&
    !emailError.value &&
    !addressError.value;

  // If manual address component is shown, also check its validity
  if (showAddressManual.value && manualAddressComponent.value) {
    return baseFormValid && Boolean(manualAddressComponent.value.isFormValid);
  }

  return baseFormValid;
}

async function submitFormAndNavigate(): Promise<void> {
  isSubmitting.value = true;
  if (validateForm()) {
    try {
      const rawPayload: EnrollmentRequest = {
        name: {
          first: firstName.value,
          last: lastName.value,
        },
        dob: dob.value,
        addresses: addresses.value,
        email_addresses: [email.value],
      };
      const payload = toApiPayload(rawPayload);
      await DataDeleteService.createEnrollmentData(payload);
      posthogCapture(PH_EVENT_USER_SUBMITTED_DD_SUBMISSION_FORM, {
        theme: colorScheme.value,
      });
      router.push({ name: "ExposureStatusEnrollMonitoring" });
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          "Error creating enrollment data. Please try again."
      );
    } finally {
      isSubmitting.value = false;
    }
  } else {
    toast.error("Please correct the errors in the form before submitting.");
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div class="enrollment-v2">
    <div class="enrollment-v2__content">
      <div class="enrollment-v2__header">
        <BaseText variant="headline-3-bold">
          Required: Confirm your data
        </BaseText>
        <BaseText variant="body-3-regular">
          This information will be used to locate exposures on data broker
          websites and secure them immediately.
        </BaseText>
      </div>
      <div class="enrollment-v2__form">
        <div class="enrollment-v2__form_names">
          <BaseInput
            ref="firstNameInput"
            v-model="firstName"
            autofocus
            :error="firstNameError"
            title="First Name*"
            placeholder="John"
            autocomplete="given-name"
            class="page-enrollment-names__first-name"
            @blur="validateFirstName"
            @input="validateFirstNameDebounced"
            @keydown.enter="lastNameInput?.focus()"
          />
          <BaseInput
            ref="lastNameInput"
            v-model="lastName"
            :error="lastNameError"
            title="Last Name*"
            placeholder="Appleseed"
            autocomplete="family-name"
            class="page-enrollment-names__last-name"
            @blur="validateLastName"
            @input="validateLastNameDebounced"
            @keydown.enter="emailInput?.focus()"
          />
        </div>
        <BaseInput
          ref="emailInput"
          v-model="email"
          :error="emailError"
          title="Email*"
          placeholder="email@domain.com"
          autocomplete="email"
          class="page-enrollment-names__email"
          @blur="validateEmail"
          @input="validateEmailDebounced"
          @keydown.enter="dobInput?.focus()"
        />
        <BaseInput
          ref="dobInput"
          v-model="dob"
          :error="dateOfBirthError"
          title="Date of Birth (MM-DD-YYYY)*"
          placeholder="02-22-1990"
          autocomplete="bday"
          inputmode="numeric"
          :maxlength="10"
          class="page-enrollment-names__dob"
          @blur="validateDateOfBirth"
          @input="validateDateOfBirthDebounced"
          @keydown.enter="addressInput?.focus()"
        />

        <EnrollmentAddressInput
          ref="addressInput"
          v-model="addressString"
          :error="addressError"
          title="Address*"
          :title-after="
            showAddressManual ? 'Use autocomplete' : `Can't find your address?`
          "
          placeholder="123 Main St Boston"
          autocomplete="street-address"
          :class="{
            'enrollment-v2__hide-input':
              showAddressManual || !!formattedPrimaryAddress,
          }"
          @input="addressError = null"
          @blur="addressError = null"
          @click-action="onAddAddress"
          @click-title-after="toggleAddressManual"
        />

        <EnrollmentTags>
          <EnrollmentTag
            v-if="formattedPrimaryAddress && !showAddressManual"
            :key="formattedPrimaryAddress.display"
            tabindex="0"
            @click="onRemoveAddress"
          >
            {{ formattedPrimaryAddress.display }}
          </EnrollmentTag>
        </EnrollmentTags>

        <PageEnrollmentAddressesManual
          v-if="showAddressManual"
          ref="manualAddressComponent"
          :is-full-page="false"
          @complete="onAddAddress"
        />

        <EnrollmentAgreement
          v-model="agreesWithTerms"
          class="page-enrollment-confirm__agreement"
          :first-name="firstName"
          :last-name="lastName"
        />
        <BaseButton
          variant="primary"
          size="lg"
          :full-width="true"
          class="enrollment-v2__button"
          :disabled="!isFormValid"
          :loading="isSubmitting"
          @click="submitFormAndNavigate"
        >
          Submit
        </BaseButton>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.enrollment-v2 {
  &__header {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__hide-input {
    :deep(.base-input__input) {
      display: none;
    }
  }

  &__content {
    max-width: 400px;
    margin: 0 auto;
    padding: 32px 16px;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 24px;

    &:deep(.base-input__input) {
      &:not(:disabled, :focus, :active, .base-input__input--error) {
        border-color: $color-primary-50;
      }
    }

    &_names {
      display: flex;
      flex-direction: row;
      gap: 16px;
      align-items: flex-start;
      justify-content: space-between;
    }
  }
}
</style>
